import React, { useState, useEffect } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import './CultureAssessment.css';

const CultureAssessment = ({ organizationData }) => {
  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const questionsPerPage = 2;
  const totalPages = Math.ceil(questions.length / questionsPerPage);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await fetch('http://localhost:3000/api/culture/questions');
      const data = await response.json();
      
      if (data.success) {
        setQuestions(data.data);
      } else {
        console.error('Failed to fetch questions:', data.message);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (questionId, score) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: score
    }));
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      // Convert responses to the format expected by the API
      const formattedResponses = Object.entries(responses).map(([questionId, score]) => {
        const question = questions.find(q => q.id === parseInt(questionId));
        return {
          questionId: parseInt(questionId),
          score: parseInt(score),
          orientation: question.orientation,
          subtheme: question.subtheme
        };
      });

      const response = await fetch('http://localhost:3000/api/culture/results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          responses: formattedResponses,
          organizationType: organizationData?.maturityStage || 'GROWTH',
          organizationName: organizationData?.organizationName || '',
          industry: organizationData?.industry || ''
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setResult(data.data);
        setCompleted(true);
      } else {
        console.error('Failed to submit assessment:', data.message);
        alert('Failed to submit assessment. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert('Error submitting assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getCultureHealthLabel = (score) => {
    if (score >= 85) return { label: 'Thriving', interpretation: 'Culture strongly supports strategy and results.', color: '#10b981' };
    if (score >= 70) return { label: 'Healthy', interpretation: 'Solid base, some areas to strengthen.', color: '#3b82f6' };
    if (score >= 55) return { label: 'Emerging', interpretation: 'Mixed culture signals; alignment needed.', color: '#f59e0b' };
    if (score >= 40) return { label: 'At-Risk', interpretation: 'Weak alignment; culture may hinder performance.', color: '#ef4444' };
    return { label: 'Misaligned', interpretation: 'Cultural breakdown; urgent attention required.', color: '#dc2626' };
  };

  const generateInterpretation = (cultureHealth, topStrength, keyImprovement, organizationType) => {
    const strengthText = `${topStrength.orientation} (${Math.round(topStrength.score)}%)`;
    const improvementText = `${keyImprovement.orientation} (${Math.round(keyImprovement.score)}%)`;
    
    const interpretations = {
      'Thriving': `Your organization demonstrates exceptional ${strengthText} capabilities, positioning you well for sustained success. Focus on maintaining this momentum while addressing ${improvementText} to achieve even greater organizational excellence.`,
      'Healthy': `Your organization shows strong ${strengthText} foundations that support your ${organizationType.toLowerCase()} objectives. To reach the next level, prioritize strengthening ${improvementText} through targeted initiatives and leadership focus.`,
      'Emerging': `Your organization has mixed signals with ${strengthText} showing promise but ${improvementText} requiring immediate attention. This suggests a need for clearer alignment and focused development efforts.`,
      'At-Risk': `Your organization faces significant challenges with ${improvementText} critically low, potentially hindering your ${organizationType.toLowerCase()} goals. Immediate intervention in ${improvementText} is essential for organizational health.`,
      'Misaligned': `Your organization requires urgent cultural transformation. While ${strengthText} shows some promise, ${improvementText} indicates fundamental misalignment that threatens organizational viability.`
    };
    
    return interpretations[cultureHealth.label] || interpretations['Healthy'];
  };

  const getCurrentQuestions = () => {
    const startIndex = currentPage * questionsPerPage;
    const endIndex = startIndex + questionsPerPage;
    return questions.slice(startIndex, endIndex);
  };

  const isCurrentPageComplete = () => {
    const currentQuestions = getCurrentQuestions();
    return currentQuestions.every(q => responses[q.id] !== undefined);
  };

  const isAllComplete = () => {
    return questions.every(q => responses[q.id] !== undefined);
  };

  if (loading) {
    return (
      <div className="culture-assessment">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading assessment questions...</p>
        </div>
      </div>
    );
  }

  if (completed && result) {
    const cultureHealth = getCultureHealthLabel(Math.round(result.cultureIndex));
    const interpretation = generateInterpretation(cultureHealth, result.topStrength, result.keyImprovement, result.organizationType);
    
    return (
      <div className="culture-assessment">
        <div className="assessment-header">
          <h1>Culture Assessment Complete!</h1>
          <p>Thank you for completing the culture assessment.</p>
        </div>
        
        <div className="results-container">
          <div className="overall-score">
            <h2>Your Culture Health Index (CHI)</h2>
            <div className="score-circle" style={{ background: `linear-gradient(135deg, ${cultureHealth.color}, ${cultureHealth.color}dd)` }}>
              <span className="score-value">{Math.round(result.cultureIndex)}</span>
              <span className="score-label">%</span>
            </div>
            <div className="culture-health-label" style={{ color: cultureHealth.color }}>
              <h3>{cultureHealth.label}</h3>
              <p>{cultureHealth.interpretation}</p>
            </div>
          </div>

          <div className="strengths-insights">
            <div className="strength-item">
              <div className="strength-icon">💪</div>
              <div className="strength-content">
                <h4>Top Strength</h4>
                <p><strong>{result.topStrength.orientation}</strong> ({Math.round(result.topStrength.score)}%)</p>
              </div>
            </div>
            <div className="improvement-item">
              <div className="improvement-icon">🎯</div>
              <div className="improvement-content">
                <h4>Key Area to Strengthen</h4>
                <p><strong>{result.keyImprovement.orientation}</strong> ({Math.round(result.keyImprovement.score)}%)</p>
              </div>
            </div>
          </div>

          <div className="interpretation-section">
            <h3>Assessment Interpretation</h3>
            <p className="interpretation-text">{interpretation}</p>
          </div>

          <div className="radar-chart-section">
            <h3>Culture Orientation Radar</h3>
            <div className="radar-container">
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart data={Object.entries(result.scores).map(([orientation, score]) => ({
                  orientation,
                  score: Math.round(score),
                  fullMark: 100
                }))}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="orientation" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar
                    name="Culture Score"
                    dataKey="score"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="orientation-scores">
            <h3>Orientation Scores</h3>
            <div className="scores-grid">
              {Object.entries(result.scores).map(([orientation, score]) => (
                <div key={orientation} className="score-item">
                  <div className="orientation-name">{orientation}</div>
                  <div className="orientation-score">{Math.round(score)}%</div>
                  <div className="score-bar">
                    <div 
                      className="score-fill" 
                      style={{ width: `${score}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="organization-type">
            <p><strong>Organization Type:</strong> {result.organizationType}</p>
          </div>
          
          <div className="culture-health-table">
            <h3>Culture Health Index Reference</h3>
            <div className="health-table">
              <div className="table-header">
                <span>Score Range</span>
                <span>Label</span>
                <span>Interpretation</span>
              </div>
              <div className="table-row" style={{ backgroundColor: Math.round(result.cultureIndex) >= 85 ? '#f0fdf4' : '#fafafa' }}>
                <span className="score-range">85–100</span>
                <span className="label thriving">Thriving</span>
                <span className="interpretation">Culture strongly supports strategy and results.</span>
              </div>
              <div className="table-row" style={{ backgroundColor: Math.round(result.cultureIndex) >= 70 && Math.round(result.cultureIndex) < 85 ? '#eff6ff' : '#fafafa' }}>
                <span className="score-range">70–84</span>
                <span className="label healthy">Healthy</span>
                <span className="interpretation">Solid base, some areas to strengthen.</span>
              </div>
              <div className="table-row" style={{ backgroundColor: Math.round(result.cultureIndex) >= 55 && Math.round(result.cultureIndex) < 70 ? '#fffbeb' : '#fafafa' }}>
                <span className="score-range">55–69</span>
                <span className="label emerging">Emerging</span>
                <span className="interpretation">Mixed culture signals; alignment needed.</span>
              </div>
              <div className="table-row" style={{ backgroundColor: Math.round(result.cultureIndex) >= 40 && Math.round(result.cultureIndex) < 55 ? '#fef2f2' : '#fafafa' }}>
                <span className="score-range">40–54</span>
                <span className="label at-risk">At-Risk</span>
                <span className="interpretation">Weak alignment; culture may hinder performance.</span>
              </div>
              <div className="table-row" style={{ backgroundColor: Math.round(result.cultureIndex) < 40 ? '#fef2f2' : '#fafafa' }}>
                <span className="score-range">&lt;40</span>
                <span className="label misaligned">Misaligned</span>
                <span className="interpretation">Cultural breakdown; urgent attention required.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="culture-assessment">
      <div className="assessment-header">
        <h1>Culture Assessment</h1>
        <p>Please rate each statement on a scale of 1-10, where 1 means "Strongly Disagree" and 10 means "Strongly Agree".</p>
        <div className="progress-info">
          <span>Page {currentPage + 1} of {totalPages}</span>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      <div className="questions-container">
        {getCurrentQuestions().map((question) => (
          <div key={question.id} className="question-card">
            <div className="question-header">
              <h3>Question {question.id}</h3>
              <div className="question-meta">
                <span className="orientation">{question.orientation}</span>
                <span className="subtheme">{question.subtheme}</span>
              </div>
            </div>
            
            <p className="question-text">{question.text}</p>
            
            <div className="rating-container">
              <div className="rating-labels">
                <span>Strongly Disagree</span>
                <span>Strongly Agree</span>
              </div>
              
              <div className="rating-scale">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                  <label key={score} className="rating-option">
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={score}
                      checked={responses[question.id] === score}
                      onChange={() => handleScoreChange(question.id, score)}
                    />
                    <span className="rating-number">{score}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="navigation">
        <button 
          onClick={handlePrevious}
          disabled={currentPage === 0}
          className="nav-button prev"
        >
          Previous
        </button>
        
        {currentPage === totalPages - 1 ? (
          <button 
            onClick={handleSubmit}
            disabled={!isAllComplete() || submitting}
            className="nav-button submit"
          >
            {submitting ? 'Submitting...' : 'Submit Assessment'}
          </button>
        ) : (
          <button 
            onClick={handleNext}
            disabled={!isCurrentPageComplete()}
            className="nav-button next"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default CultureAssessment;
